import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { X, Send, CheckCircle } from 'lucide-react'
import { useUserStore } from '@/stores/user'
import { validateEmail } from '@/utils/helpers'

interface DemoModalProps {
  isOpen?: boolean
  onClose?: () => void
}

const DemoModal: React.FC<DemoModalProps> = ({ isOpen = false, onClose }) => {
  const { t } = useTranslation()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})

  // Use selectors to subscribe to specific parts of the store
  const demoRequest = useUserStore((state) => state.demoRequest)
  const updateDemoRequest = useUserStore((state) => state.updateDemoRequest)
  const resetDemoRequest = useUserStore((state) => state.resetDemoRequest)
  const submitDemoRequest = useUserStore((state) => state.submitDemoRequest)
  
  // Calculate validation inline to ensure reactivity
  const isDemoFormValid = (() => {
    const { name, email, organization, message } = demoRequest
    const trimmedName = name?.trim()
    const trimmedEmail = email?.trim()
    const trimmedOrg = organization?.trim()
    const trimmedMessage = message?.trim()
    
    const isValid = !!(
      trimmedName && 
      trimmedEmail && 
      validateEmail(trimmedEmail) &&
      trimmedOrg && 
      trimmedMessage
    )
    
    
    return isValid
  })()


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    // Validate form
    const newErrors: Record<string, string> = {}
    
    if (!demoRequest.name.trim()) {
      newErrors.name = t('validation.required')
    }
    
    if (!demoRequest.email.trim()) {
      newErrors.email = t('validation.required')
    } else if (!validateEmail(demoRequest.email)) {
      newErrors.email = t('validation.email')
    }
    
    if (!demoRequest.organization.trim()) {
      newErrors.organization = t('validation.required')
    }
    
    // Service of Interest is optional; no validation required
    
    if (!demoRequest.message.trim()) {
      newErrors.message = t('validation.required')
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
      return
    }

    setIsSubmitting(true)
    
    try {
      const success = await submitDemoRequest()
      if (success) {
        setIsSubmitted(true)
        setTimeout(() => {
          setIsSubmitted(false)
          if (onClose) onClose()
        }, 3000)
      }
    } catch (error) {
      console.error('Failed to submit demo request:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleInputChange = (field: string, value: string) => {
    updateDemoRequest({ [field]: value })
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }))
    }
  }

  const handleClose = () => {
    if (!isSubmitting) {
      resetDemoRequest()
      setErrors({})
      if (onClose) onClose()
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div 
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={handleClose}
        />

        {/* Modal */}
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                {t('demo.title')}
              </h3>
              <button
                onClick={handleClose}
                className="text-gray-400 hover:text-gray-600 transition-colors"
                disabled={isSubmitting}
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            {isSubmitted ? (
              /* Success State */
              <div className="text-center py-8">
                <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  {t('demo.form.success')}
                </h3>
                <p className="text-gray-600">
                  We'll be in touch with you shortly to schedule your demo.
                </p>
              </div>
            ) : (
              /* Form */
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                    {t('demo.form.name')} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    value={demoRequest.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                      errors.name ? 'border-red-500' : 'border-gray-300'
                    }`}
                    disabled={isSubmitting}
                  />
                  {errors.name && (
                    <p className="mt-1 text-sm text-red-600">{errors.name}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                    {t('demo.form.email')} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    value={demoRequest.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                      errors.email ? 'border-red-500' : 
                      demoRequest.email.trim() && !validateEmail(demoRequest.email.trim()) ? 'border-red-500' :
                      demoRequest.email.trim() && validateEmail(demoRequest.email.trim()) ? 'border-green-500' :
                      'border-gray-300'
                    }`}
                    disabled={isSubmitting}
                  />
                  {errors.email && (
                    <p className="mt-1 text-sm text-red-600">{errors.email}</p>
                  )}
                  {demoRequest.email.trim() && !validateEmail(demoRequest.email.trim()) && !errors.email && (
                    <p className="mt-1 text-sm text-red-600">Please enter a valid email address</p>
                  )}
                  {demoRequest.email.trim() && validateEmail(demoRequest.email.trim()) && (
                    <p className="mt-1 text-sm text-green-600">✓ Valid email address</p>
                  )}
                </div>

                <div>
                  <label htmlFor="organization" className="block text-sm font-medium text-gray-700 mb-1">
                    {t('demo.form.organization')} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="organization"
                    value={demoRequest.organization}
                    onChange={(e) => handleInputChange('organization', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                      errors.organization ? 'border-red-500' : 'border-gray-300'
                    }`}
                    disabled={isSubmitting}
                  />
                  {errors.organization && (
                    <p className="mt-1 text-sm text-red-600">{errors.organization}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="service" className="block text-sm font-medium text-gray-700 mb-1">
                    {t('demo.form.service')}
                  </label>
                  <select
                    id="service"
                    value={demoRequest.service}
                    onChange={(e) => handleInputChange('service', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    disabled={isSubmitting}
                  >
                    <option value="">Select a service</option>
                    <option value="referralcx">ReferralCX</option>
                    <option value="schedulingcx">SchedulingCX</option>
                    <option value="econsultcx">eConsultCX</option>
                    <option value="casecx">CaseCX</option>
                    <option value="consumercx">ConsumerCX</option>
                    <option value="vaxmanagercx">VaxManagerCX</option>
                    <option value="cellcx">CellCX</option>
                    <option value="probationcx">ProbationCX</option>
                    <option value="platform">Full Platform</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                    {t('demo.form.phone')}
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    value={demoRequest.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    disabled={isSubmitting}
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
                    {t('demo.form.message')} <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    id="message"
                    rows={4}
                    value={demoRequest.message}
                    onChange={(e) => handleInputChange('message', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                      errors.message ? 'border-red-500' : 'border-gray-300'
                    }`}
                    disabled={isSubmitting}
                  />
                  {errors.message && (
                    <p className="mt-1 text-sm text-red-600">{errors.message}</p>
                  )}
                </div>

                {/* Submit Button */}
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={handleClose}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors"
                    disabled={isSubmitting}
                  >
                    {t('common.cancel')}
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting || !isDemoFormValid}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-[#1F2B59] hover:bg-[#18244C] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#1F2B59] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                        {t('common.loading')}
                      </>
                    ) : (
                      <>
                        <Send className="h-4 w-4 mr-2" />
                        {t('demo.form.submit')}
                      </>
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default DemoModal


